import React from 'react'
import type { ColumnsBlock as BlockProps } from '@/payload-types'
import { ColumnBlock } from '@/blocks/Column/Component'
import { cn } from '@/utilities/cn'

const GAP_CLASS: Record<string, string> = {
  none: 'gap-0',
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-8',
  xl: 'gap-16',
}
const ALIGN_CLASS: Record<string, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
}

// Use Tailwind's flex-grow weights via inline style.flexBasis derived
// from the column's `span`. Sticking with flex (not CSS grid) so the
// asymmetric splits scale to any number of columns without committing
// to a 12-col template the editor may not understand.

export const ColumnsBlock: React.FC<BlockProps> = ({
  layout,
  gap,
  verticalAlign,
  mobileBehaviour,
}) => {
  return (
    <div
      className={cn(
        'flex w-full',
        // mobileBehaviour=stack defaults to stacking on mobile, side-by-side
        // from md upwards. sideBySide stays in row mode at all widths.
        mobileBehaviour === 'sideBySide' ? 'flex-row' : 'flex-col md:flex-row',
        GAP_CLASS[gap || 'md'],
        ALIGN_CLASS[verticalAlign || 'start'],
      )}
    >
      {(layout ?? []).map((col, i) => {
        // Guard against schema drift: Payload's generated discriminated
        // union widens whenever a new sibling block is added. The
        // `blockType` check narrows safely to ColumnBlock and silently
        // drops anything else (which would only appear if the schema
        // changes without this code being updated).
        if (col.blockType !== 'column') return null
        return <ColumnBlock key={col.id ?? i} {...col} />
      })}
    </div>
  )
}
